import { FC, useState } from "react";
import { format } from "date-fns";
import { Button } from "@design-system/Button/Button";
import { SplitButton } from "@design-system/Button/SplitButton";
import { HStack } from "@design-system/HStack/HStack";
import { Menu } from "@design-system/Menu/Menu.lib";
import { PlainModal, StandardModalHeader } from "@design-system/Modal/Modal";
import { useCopyToClipboard } from "@hooks/useCopyToClipboard";
import { getEnv } from "@lib/environment";
import { useActiveOrganization } from "@hooks/useActiveOrganization";
import {
    useOrganizationBillingInfoQuery,
    useOrganizationOwnedPlanPricesQuery,
} from "@api/billingOrganization.api";
import { discountLabel } from "@helpers/billing";
import { CheckIcon, CopyIcon } from "@icons";
import {
    getAccountantDemoClientCountAnswer,
    SubscriptionStatus,
} from "@common/billing";
import { BookCallButton } from "@design-system/BookCall/BookCallButton";
import { fullDateFullYearFormat } from "@common/helpers/date";
import { capitalize } from "@common/helpers/string";
import { getAnonymousAccountNumber } from "@common/helpers/bankAccount";
import { useOverlays } from "contexts/OverlayContext";
import styles from "./BillingPageV2SetupSection.module.scss";
import { useUser } from "hooks/useUser";

const AIP_DEMO_CALL_URL =
    "https://calendly.com/d/cxwj-2fv-9sf?utm_source=app&utm_content=billing";

const AIP_BENEFITS = [
    "50% off all licenses",
    "Free migrations",
    "1:1 Kick certification",
    "Client referrals",
];

interface BillingPageV2SetupSectionProps {
    canManageBilling: boolean;
}

export const BillingPageV2SetupSection: FC<BillingPageV2SetupSectionProps> = ({
    canManageBilling,
}) => {
    const { openOverlay } = useOverlays();
    const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
    const copyToClipboard = useCopyToClipboard();
    const activeOrganization = useActiveOrganization();
    const isAipPartner = !!activeOrganization?.aipProgramSince;
    const hasSubscription = !!activeOrganization?.billing?.subscriptionId;
    const isCancelled =
        activeOrganization?.billing?.subscriptionStatus ===
        SubscriptionStatus.CANCELLED;
    const isEnrolled = !!activeOrganization?.billing?.setupIntentId;
    const cancelledAt = activeOrganization?.billing?.cancelledAt;
    const {
        email,
        firstName,
        lastName,
        preferredName,
        legalName,
        unverifiedPhoneNumber,
    } = useUser();
    const setupLabel = isAipPartner
        ? "AI-Native Program"
        : "Self-serve Accountant";

    const { data: billingInfo } = useOrganizationBillingInfoQuery(
        activeOrganization?.id,
    );

    const { data: planPrices } = useOrganizationOwnedPlanPricesQuery(
        activeOrganization?.id,
    );
    const discount = planPrices?.discount;
    const discountValue = discount ? discountLabel(discount) : "None";

    const billingEmail = billingInfo?.email;
    const billingName = billingInfo?.name;
    const paymentMethod = billingInfo?.paymentMethod;
    const shouldShowSetupBillingButton =
        !isEnrolled && !hasSubscription && canManageBilling;

    return (
        <div className={styles.container}>
            <div className={styles.sectionTitle}>Your setup</div>
            <div className={styles.cardContainer}>
                <div className={styles.sectionHeader}>{setupLabel}</div>
                <div className={styles.setupGrid}>
                    <div className={styles.setupLabel}>Discount</div>
                    <div className={styles.setupValue}>
                        {planPrices ? discountValue : "-"}
                    </div>
                    {billingName && (
                        <>
                            <div className={styles.setupLabel}>Name</div>
                            <div className={styles.setupValue}>
                                {billingName}
                            </div>
                        </>
                    )}
                    {billingEmail && (
                        <>
                            <div className={styles.setupLabel}>
                                Billing email
                            </div>
                            <div className={styles.setupValue}>
                                {billingEmail}
                            </div>
                        </>
                    )}
                    {isCancelled && (
                        <>
                            <div className={styles.setupLabel}>Status</div>
                            <div className={styles.setupValue}>
                                <HStack gap={8} align="center">
                                    <span>
                                        {cancelledAt
                                            ? format(
                                                  cancelledAt,
                                                  fullDateFullYearFormat,
                                              )
                                            : "-"}
                                    </span>
                                    {canManageBilling && (
                                        <Button
                                            size="sm"
                                            variant="primary"
                                            onClick={() =>
                                                openOverlay(
                                                    "enrollOrganization",
                                                    {},
                                                )
                                            }
                                        >
                                            Reactivate
                                        </Button>
                                    )}
                                </HStack>
                            </div>
                        </>
                    )}

                    {hasSubscription && canManageBilling && !isCancelled && (
                        <>
                            <div className={styles.setupLabel}>
                                Payment details
                            </div>
                            <HStack
                                gap={12}
                                align="center"
                                className={styles.setupValue}
                            >
                                {paymentMethod && (
                                    <>
                                        {capitalize(
                                            (
                                                paymentMethod.brand ?? ""
                                            ).toLowerCase(),
                                        )}{" "}
                                        {getAnonymousAccountNumber(
                                            paymentMethod.last4,
                                            2,
                                            "",
                                        )}
                                    </>
                                )}
                                <SplitButton
                                    size="sm"
                                    variant="primary"
                                    menuAriaLabel="Payment details actions"
                                    onPrimaryClick={() => {
                                        window.open(
                                            getEnv("VITE_STRIPE_PORTAL_URL"),
                                            "_blank",
                                            "noopener,noreferrer",
                                        );
                                    }}
                                    menu={
                                        <Menu.Item
                                            onClick={() =>
                                                setIsCancelModalOpen(true)
                                            }
                                        >
                                            Cancel subscription
                                        </Menu.Item>
                                    }
                                >
                                    Edit
                                </SplitButton>
                            </HStack>
                        </>
                    )}
                    {shouldShowSetupBillingButton && (
                        <>
                            <div className={styles.setupLabel}>
                                Payment details
                            </div>
                            <div className={styles.setupValue}>
                                <Button
                                    size="sm"
                                    variant="primary"
                                    onClick={() =>
                                        openOverlay("enrollOrganization", {})
                                    }
                                >
                                    Add payment details
                                </Button>
                            </div>
                        </>
                    )}
                </div>
                {!isAipPartner ? (
                    <div className={styles.aipPromoContainer}>
                        <div className={styles.aipPromoTitleContainer}>
                            <div className={styles.title}>
                                The AI-Native Program
                            </div>
                            <div className={styles.spots}>*Limited spots</div>
                        </div>
                        <div className={styles.aipBenefitsGrid}>
                            {AIP_BENEFITS.map((benefit) => (
                                <div
                                    key={benefit}
                                    className={styles.aipBenefitItem}
                                >
                                    <CheckIcon
                                        className={styles.aipBenefitIcon}
                                    />
                                    <span>{benefit}</span>
                                </div>
                            ))}
                        </div>
                        <div className={styles.aipPromoBookingContainer}>
                            <BookCallButton
                                variant="primary"
                                size="sm"
                                url={AIP_DEMO_CALL_URL}
                                data-testid="book-call-button"
                                customAnswers={{
                                    a1:
                                        preferredName ??
                                        legalName ??
                                        `${firstName} ${lastName}`,
                                    a2: email ?? "",
                                    a3: unverifiedPhoneNumber ?? "",
                                    a4: getAccountantDemoClientCountAnswer(
                                        activeOrganization?.profile?.firmSize,
                                    ),
                                }}
                            >
                                Book a demo
                            </BookCallButton>
                            <div className={styles.aipPromoPrice}>
                                Starts at $6k
                            </div>
                        </div>
                    </div>
                ) : null}
            </div>
            <PlainModal
                show={isCancelModalOpen}
                onClose={() => setIsCancelModalOpen(false)}
            >
                <StandardModalHeader>Cancel subscription</StandardModalHeader>
                <HStack gap={8} align="center">
                    <span>
                        To cancel your subscription, email us at{" "}
                        <span className={styles.supportEmail}>
                            support@kick.co
                        </span>
                    </span>
                    <Button
                        variant="secondary"
                        size="md"
                        iconOnly
                        aria-label="Copy email"
                        onClick={() => {
                            void copyToClipboard("support@kick.co");
                        }}
                    >
                        <CopyIcon />
                    </Button>
                </HStack>
            </PlainModal>
        </div>
    );
};
